// An assistant turn as an ordered list of parts, rather than one markdown string
// plus a bag of chips appended at the end.
//
// Why order matters, and why it is free: rgip's DeliverableStreamEmitter splits the
// token stream into prose and deliverable blocks and emits them INTERLEAVED, in the
// order the model wrote them, holding back only a buffer tail that could still grow
// into a tag. `studio_jump` activities arrive interleaved too. So the panel does not
// have to reconstruct position, only stop throwing it away — which is what it did
// before: every jump chip was pushed onto one array and rendered after the whole
// answer, so an agent that wrote fifteen splits produced fifteen chips in a heap at
// the bottom, detached from the sentences they belonged to.
//
// Every function here is pure and returns a new array, so a turn's parts can be
// rebuilt from a recorded frame sequence in a test with no DOM and no fetch.
//
// A block's `key` names a SLOT, not an append point: re-emitting a key replaces
// that block in place, keeping its position. That is rgip's own contract (a reused
// key is a new version of the same deliverable) and it is what lets an agent
// restate a plan as it works without stacking up stale copies of it.
//
// NOT solved here: a REOPENED thread cannot be rebuilt this way. The transcript
// endpoint returns prose-only content plus `steps` that carry no position relative
// to that prose, and deliverables come from a separate endpoint with no ordering
// either. Replaying a past turn faithfully needs rgip to persist part order.

import { ScoreCardData } from './scoreCardData';
import { SignalCardData } from './signalCardData';
import {
  BaseCardData,
  RulesCardData,
  ThresholdsCardData,
  ThresholdsComparison,
  mergeThresholds,
} from './rulesCardData';
import { SplitCardData } from './splitCardData';

/** How a block's body should be rendered. Mirrors rgip's `FORMATS`. */
export type BlockFormat = 'markdown' | 'html';

export type ChatPart =
  | { kind: 'text'; text: string }
  | { kind: 'pill'; targetType: string; targetId: string; label: string }
  | {
      kind: 'card';
      card: SplitCardData;
      /** Position in this build, 1-based. The receipt cannot know it; the turn does. */
      splitIndex: number;
    }
  | { kind: 'scoreCard'; card: ScoreCardData }
  | { kind: 'signalCard'; card: SignalCardData }
  | { kind: 'rulesCard'; card: RulesCardData }
  | { kind: 'baseCard'; card: BaseCardData }
  | { kind: 'thresholdsCard'; comparison: ThresholdsComparison }
  | {
      kind: 'block';
      /** The agent-chosen `key` from the deliverable tag. Selects the renderer. */
      key: string;
      /** rgip's `kind` vocabulary (document, email, structured, ...). */
      blockKind: string;
      title: string;
      format: BlockFormat;
      /** Body as streamed. A `structured` block's body is JSON as text: rgip's
       *  `content_json` column exists but the chat path never populates it. */
      raw: string;
      /** False while `artifact_delta` frames may still arrive. */
      done: boolean;
      /** Set from the `artifact` frame once the block is persisted, so a part can
       *  be re-fetched later. That frame carries meta only, never the content. */
      deliverableId?: string;
    };

export interface BlockOpen {
  key: string;
  kind: string;
  title: string;
  format: BlockFormat;
}

const BLOCK_FORMATS: BlockFormat[] = ['markdown', 'html'];

/** Read a format off the wire, defaulting the way rgip does. */
export function blockFormat(raw: unknown): BlockFormat {
  return BLOCK_FORMATS.includes(raw as BlockFormat)
    ? (raw as BlockFormat)
    : 'markdown';
}

/**
 * Index of the block slot a `key` names, open or closed.
 *
 * A key is a SLOT in the turn, not an append point. rgip's own contract says a
 * reused key is a new version of the same deliverable, so an agent that re-emits
 * its plan after three more splits means "the plan now reads like this", not "here
 * is a second plan". Stacking them would grow a column of stale copies.
 */
function blockSlot(parts: ChatPart[], key: string): number {
  return parts.findIndex((part) => part.kind === 'block' && part.key === key);
}

/**
 * Append an answer token.
 *
 * Merges into the trailing text part so prose either side of a block stays one
 * paragraph for the markdown parser: splitting it would break a list or a table
 * that happens to straddle a token boundary.
 */
export function appendText(parts: ChatPart[], delta: string): ChatPart[] {
  if (!delta) return parts;
  const last = parts[parts.length - 1];
  if (last?.kind === 'text') {
    return [...parts.slice(0, -1), { kind: 'text', text: last.text + delta }];
  }
  return [...parts, { kind: 'text', text: delta }];
}

/**
 * Place a written split's card where the write landed.
 *
 * Same reasoning as `appendPill`, and the reason cards are parts rather than a list
 * rendered after the turn: a build that lands eleven splits would otherwise stack
 * eleven cards at the bottom, detached from the branch each one is about.
 */
export function appendCard(parts: ChatPart[], card: SplitCardData): ChatPart[] {
  // Numbered here because only the turn knows how many splits preceded this one. The
  // receipt describes one write and has no idea it is the fourth.
  const splitIndex = parts.filter((part) => part.kind === 'card').length + 1;
  return [...parts, { kind: 'card', card, splitIndex }];
}

/**
 * Place a scored record's card where the lookup happened.
 *
 * Unnumbered, unlike a split: a turn lands one lookup, and "Score 1" on a card with no
 * Score 2 beside it labels a sequence that does not exist.
 */
export function appendScoreCard(
  parts: ChatPart[],
  card: ScoreCardData
): ChatPart[] {
  return [...parts, { kind: 'scoreCard', card }];
}

/** Place a signals write's card where the write happened. */
export function appendSignalCard(
  parts: ChatPart[],
  card: SignalCardData
): ChatPart[] {
  return [...parts, { kind: 'signalCard', card }];
}

/** Place a rules write's receipt, or a base switch's, where it happened. */
export function appendRulesCard(
  parts: ChatPart[],
  card: RulesCardData | BaseCardData
): ChatPart[] {
  return card.kind === 'rules'
    ? [...parts, { kind: 'rulesCard', card }]
    : [...parts, { kind: 'baseCard', card }];
}

/**
 * Add a threshold preview to the turn's ONE comparison, or start it.
 *
 * Merged in place rather than appended: the value of a comparison is that the candidates
 * sit in adjacent columns, and a second card below the first is the stacked-paragraphs
 * problem again. See `mergeThresholds`.
 */
export function appendThresholdsCard(
  parts: ChatPart[],
  card: ThresholdsCardData
): ChatPart[] {
  const at = parts.findIndex((part) => part.kind === 'thresholdsCard');
  if (at === -1) {
    return [
      ...parts,
      { kind: 'thresholdsCard', comparison: mergeThresholds(null, card) },
    ];
  }
  const existing = parts[at] as Extract<ChatPart, { kind: 'thresholdsCard' }>;
  const next = [...parts];
  next[at] = {
    kind: 'thresholdsCard',
    comparison: mergeThresholds(existing.comparison, card),
  };
  return next;
}

/** Place a jump pill where it arrived in the answer. */
export function appendPill(
  parts: ChatPart[],
  pill: { targetType: string; targetId: string; label: string }
): ChatPart[] {
  return [...parts, { kind: 'pill', ...pill }];
}

/**
 * Open the block for this key: a new part at the current position, or the existing
 * slot reset to receive a fresh body.
 *
 * Reset rather than append, per `blockSlot`. The slot keeps its ORIGINAL position:
 * a plan stated at the top of a turn belongs at the top when it is restated, not
 * shuffled to the bottom each time it changes. `deliverableId` is dropped with the
 * old body, since the incoming version has not been persisted yet.
 */
export function openBlock(parts: ChatPart[], open: BlockOpen): ChatPart[] {
  const fresh: ChatPart = {
    kind: 'block',
    key: open.key,
    blockKind: open.kind,
    title: open.title,
    format: open.format,
    raw: '',
    done: false,
  };
  const slot = blockSlot(parts, open.key);
  if (slot === -1) return [...parts, fresh];
  const next = [...parts];
  next[slot] = fresh;
  return next;
}

/**
 * Append to a block's body.
 *
 * Two anomalies, resolved in opposite directions because the costs differ:
 *
 * - No slot for this key at all: open one. A start frame went missing, and losing
 *   the body silently is worse than an untitled card; the user still sees what the
 *   agent produced.
 * - The slot exists but is already closed: drop the delta. A stray late frame must
 *   not overwrite a finished block the user is already reading, and re-opening the
 *   slot would blank it.
 */
export function appendBlockDelta(
  parts: ChatPart[],
  key: string,
  delta: string
): ChatPart[] {
  if (!delta) return parts;
  const index = blockSlot(parts, key);
  if (index === -1)
    return appendBlockDelta(
      openBlock(parts, { key, kind: '', title: '', format: 'markdown' }),
      key,
      delta
    );
  const block = parts[index];
  if (block.kind !== 'block' || block.done) return parts;
  const next = [...parts];
  next[index] = { ...block, raw: block.raw + delta };
  return next;
}

/** Mark a block finished, so it stops accepting deltas and can render as final. */
export function closeBlock(parts: ChatPart[], key: string): ChatPart[] {
  const index = blockSlot(parts, key);
  if (index === -1) return parts;
  const block = parts[index];
  if (block.kind !== 'block') return parts;
  const next = [...parts];
  next[index] = { ...block, done: true, raw: block.raw.trim() };
  return next;
}

/**
 * Bind a persisted deliverable id onto the block it belongs to.
 *
 * The `artifact` frame arrives after the block has closed, so this looks for the
 * last block with that key still lacking an id rather than an open one.
 */
export function bindDeliverable(
  parts: ChatPart[],
  key: string,
  deliverableId: string
): ChatPart[] {
  const index = blockSlot(parts, key);
  if (index === -1) return parts;
  const block = parts[index];
  if (block.kind !== 'block') return parts;
  const next = [...parts];
  next[index] = { ...block, deliverableId };
  return next;
}

/**
 * Replace a block's body with the one the server stored.
 *
 * A `structured` block whose numbers are merged in server-side (rgip
 * `app/runs/authored.py`) streams as the model's fragment and is only complete once
 * persisted, because the `artifact` event carries meta and no content. So the panel
 * streams what the model typed, then swaps in the stored body when it can fetch it.
 * Without this the card renders from a payload missing every field it exists to show.
 */
export function replaceBlockBody(
  parts: ChatPart[],
  key: string,
  raw: string
): ChatPart[] {
  const index = blockSlot(parts, key);
  if (index === -1) return parts;
  const block = parts[index];
  if (block.kind !== 'block') return parts;
  const next = [...parts];
  next[index] = { ...block, raw, done: true };
  return next;
}

/**
 * The turn's prose, blocks and pills excluded.
 *
 * For the places that still need one string: deciding whether a turn has said
 * anything yet, and seeding a follow-up turn's context.
 */
export function partsProse(parts: ChatPart[]): string {
  return parts
    .filter((p): p is { kind: 'text'; text: string } => p.kind === 'text')
    .map((p) => p.text)
    .join('');
}

/** Whether the turn has produced anything renderable at all. */
export function hasContent(parts: ChatPart[]): boolean {
  return parts.some((p) => (p.kind === 'text' ? p.text.trim() !== '' : true));
}

/**
 * The Studio object an inline markdown link points at, or null when it points
 * somewhere else.
 *
 * `[the catch-all bucket](studio:tree_node/2:15)` in the agent's prose becomes a
 * clickable reference inside the sentence that names it.
 *
 * Why this exists rather than more `studio_focus` pills: the prompt already tells
 * the agent to call that tool "as you write the sentence, not in a batch
 * afterwards", and it batches them anyway, so eight references pile up above the
 * answer instead of sitting in it. That is a rule about WHEN to call a tool, and
 * those do not hold. A link is a rule about what the sentence CONTAINS, which does.
 *
 * Parsing is strict because the href reaches here from a model: the type is a bare
 * word, the id has no slashes, and anything else is not a Studio target.
 */
export function studioTarget(
  href: string
): { targetType: string; targetId: string } | null {
  const match = /^studio:([a-z_]+)\/([A-Za-z0-9:_-]+)$/.exec(href.trim());
  if (!match) return null;
  return { targetType: match[1], targetId: match[2] };
}

/**
 * A link href as it should reach the DOM, given the markdown renderer's own URL
 * transformer as `fallback`.
 *
 * react-markdown runs every href through `uriTransformer`, which permits http,
 * https, mailto and tel and rewrites everything else to `javascript:void(0)`. A
 * `studio:` reference is "everything else", so it arrived rewritten, failed
 * `studioTarget`, rendered as an ordinary external link and opened a blank tab on
 * click. Ours passes through; every other scheme keeps the library's protection.
 *
 * Takes the fallback as an argument rather than importing it, so the rule is
 * testable without pulling an ESM renderer into the test bundle.
 */
export function studioLinkUri(
  uri: string,
  fallback: (value: string) => string
): string {
  return studioTarget(uri) ? uri : fallback(uri);
}

/**
 * Whether an href is safe to render as a link at all.
 *
 * A transformer that blocks a scheme hands back `javascript:void(0)` rather than
 * nothing, which is inert but still navigable: with `target="_blank"` it opens an
 * empty tab, which looks exactly like the app breaking. Such a link is better
 * rendered as its own text.
 */
export function isRenderableHref(href: string | undefined): boolean {
  if (!href) return false;
  return !/^javascript:/i.test(href.trim());
}

/**
 * Whether a turn's offer belongs to a model other than the one on screen.
 *
 * A turn that duplicates a live model ends by offering to make the change on the copy,
 * and that offer renders on the ORIGINAL, where pressing it can do nothing but earn a
 * refusal: a change has to name the model the reader has open. So the buttons are held
 * back there and the turn reads as a question waiting on the other side, with the jump
 * chip beside it as the way to get there.
 *
 * True only when the turn carries a jump to a DIFFERENT model. A turn with no model
 * jump, or one whose jump names the model already open, offers what it offers — which
 * is what brings the buttons back once the conversation follows the copy.
 */
export function offersAnotherModel(
  parts: ChatPart[] | undefined,
  modelId: string
): boolean {
  return (parts ?? []).some(
    (part) =>
      part.kind === 'pill' &&
      part.targetType === 'model' &&
      String(part.targetId) !== String(modelId)
  );
}
